<script lang="ts">
  import { onDestroy, onMount, tick } from "svelte";
  import {
    ENGINE_IDS,
    ENGINE_NAMES,
    createEngine,
    loadEngineVersions,
    type CompareEngine,
    type EngineId,
    type EngineVersions,
  } from "../compare-engines";
  import { fileNameFromFileArg, readNgFile } from "../ngstore";

  // /compare/?file=/uploads/<name>&page=<n>: one page of a PDF as SumatraPDF,
  // mupdf, pdfium and pdf.js render it, side by side

  const ENABLED_KEY = "sumatra-compare-engines";

  interface EngineState {
    engine: CompareEngine | null;
    opened: Promise<number> | null;
    // what the cell should show; a render that finishes after it changed is redone
    want: string;
    rendering: boolean;
    status: string;
    error: string;
  }

  const params = new URLSearchParams(window.location.search);
  const fileName = fileNameFromFileArg(params.get("file") ?? "");

  let enabled = $state<Record<EngineId, boolean>>(loadEnabled());
  let pageNo = $state(Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1));
  let pageCount = $state(0);
  let pageInput = $state("");
  let error = $state("");
  let versions = $state<EngineVersions>({ sumatra: "", mupdf: "", pdfium: "", pdfjs: "" });
  let gridEl = $state<HTMLDivElement | null>(null);
  let gridW = $state(0);
  let gridH = $state(0);
  const canvases: Partial<Record<EngineId, HTMLCanvasElement>> = $state({});
  const engines: Record<EngineId, EngineState> = $state({
    sumatra: newEngineState(),
    mupdf: newEngineState(),
    pdfium: newEngineState(),
    pdfjs: newEngineState(),
  });

  let docBytes: Uint8Array | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let renderTimer = 0;

  const visible = $derived(ENGINE_IDS.filter((id) => enabled[id]));
  const gridStyle = $derived(layoutStyle(visible.length, gridW, gridH));

  function newEngineState(): EngineState {
    return { engine: null, opened: null, want: "", rendering: false, status: "", error: "" };
  }

  function loadEnabled(): Record<EngineId, boolean> {
    const all = { sumatra: true, mupdf: true, pdfium: true, pdfjs: true };
    try {
      const v = JSON.parse(localStorage.getItem(ENABLED_KEY) ?? "null");
      if (v && typeof v === "object") {
        for (const id of ENGINE_IDS) {
          if (typeof v[id] === "boolean") {
            all[id] = v[id];
          }
        }
      }
    } catch {
      // all engines then
    }
    return all;
  }

  function saveEnabled() {
    try {
      localStorage.setItem(ENABLED_KEY, JSON.stringify(enabled));
    } catch {
      // not remembered, that's all
    }
  }

  // 4: a 2x2 grid. 2 or 3: stacked if the area is taller than wide, side by side otherwise
  function layoutStyle(n: number, w: number, h: number): string {
    if (n === 4) {
      return "grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr";
    }
    if (n <= 1) {
      return "grid-template-columns: 1fr; grid-template-rows: 1fr";
    }
    if (h > w) {
      return `grid-template-columns: 1fr; grid-template-rows: repeat(${n}, 1fr)`;
    }
    return `grid-template-columns: repeat(${n}, 1fr); grid-template-rows: 1fr`;
  }

  function engineLabel(id: EngineId): string {
    const v = versions[id];
    return v ? `${ENGINE_NAMES[id]} ${v}` : ENGINE_NAMES[id];
  }

  function ensureOpened(id: EngineId): Promise<number> | null {
    const st = engines[id];
    if (st.opened) {
      return st.opened;
    }
    if (!docBytes) {
      return null;
    }
    st.status = "Loading...";
    st.error = "";
    const engine = createEngine(id);
    st.engine = engine;
    st.opened = engine.open(docBytes);
    st.opened.then(
      (n) => {
        if (n > pageCount) {
          pageCount = n;
        }
        scheduleRender(0);
      },
      (e) => {
        st.error = e instanceof Error ? e.message : String(e);
        st.status = "";
      },
    );
    return st.opened;
  }

  function scheduleRender(delay: number) {
    clearTimeout(renderTimer);
    renderTimer = window.setTimeout(() => {
      for (const id of visible) {
        void renderEngine(id);
      }
    }, delay);
  }

  async function renderEngine(id: EngineId) {
    const st = engines[id];
    const canvas = canvases[id];
    if (!canvas || !enabled[id] || st.rendering) {
      return;
    }
    const opened = ensureOpened(id);
    if (!opened) {
      return;
    }
    let n: number;
    try {
      n = await opened;
    } catch {
      return;
    }
    const dpr = window.devicePixelRatio || 1;
    const dx = Math.max(1, Math.round(canvas.clientWidth * dpr));
    const dy = Math.max(1, Math.round(canvas.clientHeight * dpr));
    const page = pageNo;
    const want = `${page}:${dx}x${dy}`;
    if (want === st.want) {
      return;
    }
    if (page > n) {
      st.want = want;
      st.error = `the document has only ${n} pages here`;
      clearCanvas(canvas, dx, dy);
      return;
    }
    st.rendering = true;
    st.status = "Rendering...";
    st.error = "";
    const started = performance.now();
    try {
      const r = await st.engine!.render(page, dx, dy);
      const ms = Math.round(performance.now() - started);
      clearCanvas(canvas, dx, dy);
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(r.image, Math.floor((dx - r.width) / 2), Math.floor((dy - r.height) / 2));
      if (r.image instanceof ImageBitmap) {
        r.image.close();
      }
      st.want = want;
      st.status = `${ms} ms, ${r.width}x${r.height}`;
    } catch (e) {
      st.want = want;
      st.status = "";
      st.error = e instanceof Error ? e.message : String(e);
      clearCanvas(canvas, dx, dy);
    } finally {
      st.rendering = false;
    }
    // the page or the size changed while it rendered
    void renderEngine(id);
  }

  function clearCanvas(canvas: HTMLCanvasElement, dx: number, dy: number) {
    canvas.width = dx;
    canvas.height = dy;
  }

  function goToPage(n: number) {
    const max = pageCount || n;
    const next = Math.min(Math.max(1, n), max);
    pageInput = String(next);
    if (next === pageNo) {
      return;
    }
    pageNo = next;
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(next));
    history.replaceState(history.state, "", url);
    scheduleRender(0);
  }

  function onPageInput(event: Event) {
    event.preventDefault();
    const n = parseInt(pageInput, 10);
    goToPage(Number.isFinite(n) ? n : pageNo);
  }

  async function toggleEngine(id: EngineId, on: boolean) {
    enabled[id] = on;
    saveEnabled();
    if (!on) {
      engines[id].want = "";
    }
    await tick();
    observeCells();
    scheduleRender(0);
  }

  function observeCells() {
    if (!resizeObserver) {
      return;
    }
    resizeObserver.disconnect();
    if (gridEl) {
      resizeObserver.observe(gridEl);
    }
    for (const id of visible) {
      const c = canvases[id];
      if (c) {
        resizeObserver.observe(c);
      }
    }
  }

  function onKeyDown(event: KeyboardEvent) {
    const t = event.target;
    if (t instanceof HTMLInputElement && t.type !== "checkbox") {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    switch (event.key) {
      case "ArrowLeft":
      case "ArrowUp":
      case "PageUp":
        goToPage(pageNo - 1);
        break;
      case "ArrowRight":
      case "ArrowDown":
      case "PageDown":
      case " ":
        goToPage(pageNo + 1);
        break;
      case "Home":
        goToPage(1);
        break;
      case "End":
        goToPage(pageCount);
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  onMount(() => {
    pageInput = String(pageNo);
    resizeObserver = new ResizeObserver(() => {
      if (gridEl) {
        gridW = gridEl.clientWidth;
        gridH = gridEl.clientHeight;
      }
      scheduleRender(150);
    });
    observeCells();
    void loadEngineVersions().then((v) => (versions = v));
    if (!fileName) {
      error = "No document. Open this page from the context menu of a PDF on the home page.";
      return;
    }
    document.title = `${fileName} - Compare PDF rendering engines`;
    void (async () => {
      try {
        docBytes = await readNgFile(fileName);
      } catch (e) {
        error = `Couldn't read ${fileName}: ${e instanceof Error ? e.message : String(e)}`;
        return;
      }
      scheduleRender(0);
    })();
  });

  onDestroy(() => {
    clearTimeout(renderTimer);
    resizeObserver?.disconnect();
    for (const id of ENGINE_IDS) {
      engines[id].engine?.destroy();
    }
  });
</script>

<svelte:window onkeydown={onKeyDown} />

<div class="compare">
  <div class="compare-toolbar">
    <a class="compare-home" href="/" title="Home" aria-label="Home">
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
          d="M3 10.5 12 3l9 7.5M5 9v11a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </a>
    <form class="compare-page" onsubmit={onPageInput}>
      <input
        class="compare-page-input"
        aria-label="Page number"
        inputmode="numeric"
        bind:value={pageInput}
        onblur={onPageInput}
        size={Math.max(2, String(pageCount).length)}
      />
      <span>/ {pageCount || "?"}</span>
    </form>
    <button
      class="compare-nav"
      type="button"
      title="Previous page"
      aria-label="Previous page"
      disabled={pageNo <= 1}
      onclick={() => goToPage(pageNo - 1)}
    >
      ←
    </button>
    <button
      class="compare-nav"
      type="button"
      title="Next page"
      aria-label="Next page"
      disabled={pageCount > 0 && pageNo >= pageCount}
      onclick={() => goToPage(pageNo + 1)}
    >
      →
    </button>
    {#if fileName}
      <span class="compare-file" title={fileName}>{fileName}</span>
    {/if}
    <div class="compare-space"></div>
    {#each ENGINE_IDS as id (id)}
      <label class="compare-check">
        <input
          type="checkbox"
          checked={enabled[id]}
          onchange={(event) => void toggleEngine(id, event.currentTarget.checked)}
        />
        {ENGINE_NAMES[id]}
      </label>
    {/each}
  </div>

  {#if error}
    <div class="compare-error">{error}</div>
  {:else}
    <div class="compare-grid" bind:this={gridEl} style={gridStyle}>
      {#each visible as id (id)}
        <div class="compare-cell">
          <canvas class="compare-canvas" bind:this={canvases[id]}></canvas>
          <div class="compare-label">
            <span class="compare-label-name">{engineLabel(id)}</span>
            {#if engines[id].error}
              <span class="compare-label-error">{engines[id].error}</span>
            {:else if engines[id].status}
              <span class="compare-label-status">{engines[id].status}</span>
            {/if}
          </div>
        </div>
      {:else}
        <div class="compare-empty">Check an engine to see its rendering</div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .compare {
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    background: #f7f7f4;
    color: #1f2937;
    font: 14px system-ui, sans-serif;
  }
  .compare-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-bottom: 1px solid #d6d6d0;
    background: #fff;
    min-height: 40px;
    flex-wrap: wrap;
  }
  .compare-home {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    color: inherit;
  }
  .compare-home:hover,
  .compare-nav:hover:not(:disabled) {
    background: #ececea;
  }
  .compare-page {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .compare-page-input {
    text-align: right;
    padding: 2px 4px;
    border: 1px solid #c8c8c2;
    border-radius: 4px;
    background: #fff;
    color: inherit;
    font: inherit;
  }
  .compare-nav {
    width: 30px;
    height: 30px;
    border: 1px solid #c8c8c2;
    border-radius: 6px;
    background: #fff;
    color: inherit;
    font-size: 16px;
    cursor: pointer;
  }
  .compare-nav:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .compare-file {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 30vw;
    color: #6b7280;
  }
  .compare-space {
    flex: 1;
  }
  .compare-check {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
  }
  .compare-grid {
    flex: 1;
    min-height: 0;
    display: grid;
    gap: 1px;
    background: #d6d6d0;
  }
  .compare-cell {
    position: relative;
    min-width: 0;
    min-height: 0;
    background: #e9e9e5;
  }
  .compare-canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
  .compare-label {
    position: absolute;
    left: 6px;
    top: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 3px 7px;
    border-radius: 4px;
    background: rgb(0 0 0 / 0.65);
    color: #fff;
    font-size: 12px;
    pointer-events: none;
    max-width: calc(100% - 12px);
  }
  .compare-label-name {
    font-weight: 600;
  }
  .compare-label-status {
    opacity: 0.8;
  }
  .compare-label-error {
    color: #fca5a5;
  }
  .compare-empty,
  .compare-error {
    margin: auto;
    padding: 48px;
    text-align: center;
    color: #6b7280;
  }
  .compare-empty {
    background: #e9e9e5;
  }
  .compare-error {
    color: #b91c1c;
  }
</style>
